import { Dispatch, memo, SetStateAction, useMemo } from 'react';

import { useLanguage } from 'assets/languages';
import { CategoryDto } from 'api/repositories/category/category.types';
import useGetBrand from 'hooks/useGetBrand/useGetBrand';
import useGetCategories from 'hooks/useGetCategories/useGetCategories';
import Select from 'components/select';
import TableLabel from '../tableLabel/tableLabel';

interface SelectCategoryProps {
  /** 셋업 여부 */
  isSetupClothes: boolean;
  /** 상위 카테고리 */
  selectedMainCategory: CategoryDto;
  setSelectedMainCategory: Dispatch<SetStateAction<CategoryDto>>;
  /** 하위 카테고리 */
  selectedSubCategory: CategoryDto;
  setSelectedSubCategory: Dispatch<SetStateAction<CategoryDto>>;
}

// TODO: props -> react-hook-form으로 변경 필요
function SelectCategories({
  isSetupClothes = false,
  selectedMainCategory,
  setSelectedMainCategory,
  selectedSubCategory,
  setSelectedSubCategory,
}: SelectCategoryProps) {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.component.createProductMasterModal };

  const { data: brand } = useGetBrand();
  const { data: categories } = useGetCategories({
    params: {
      brandCategoryId: brand?.categoryId,
    },
    options: {
      enabled: !!brand?.categoryId,
    },
  });

  const parentCategories = useMemo(() => {
    return categories?.filter((category) => {
      if (isSetupClothes) {
        return category.parentId === 0 && category.langKo === '셋업';
      } else {
        return category.parentId === 0 && category.langKo !== '셋업';
      }
    });
  }, [categories]);

  const childCategories = selectedMainCategory
    ? categories?.filter((category) => {
        return category.parentId === selectedMainCategory.id;
      })
    : [];

  const onChangeCategory = ({
    categoryName,
    isParent = false,
  }: {
    categoryName: CategoryDto['name'];
    isParent?: boolean;
  }) => {
    const selected = categories.find((category) => {
      return (
        (isParent ? category.parentId === 0 : category.parentId !== 0) && // 동명의 부모-자식 카테고리명 오류 방어
        category.name === categoryName
      );
    });

    if (isParent) {
      setSelectedMainCategory(selected);
    } else {
      setSelectedSubCategory(selected);
    }
  };

  return (
    <>
      <tr>
        <td className="w-[150px] bg-[#F4F4F4]">
          <TableLabel>{languageTexts.category}</TableLabel>
        </td>

        {/* 상위 카테고리 */}
        <td className="w-[calc(100%-150px)] border-b border-[#E8E8E8]">
          <div className="flex items-center py-[10px]">
            <div className="w-[150px]">
              <TableLabel>{languageTexts.parent_category}</TableLabel>
            </div>

            <div className="w-[calc(100%-150px)]">
              <Select
                selectClassName="text-[15px] py-[9px] px-[10px]"
                bordered
                placeholder={languageTexts.select}
                options={parentCategories}
                selected={selectedMainCategory?.name || ''}
                value={selectedMainCategory?.name || ''}
                onChange={(value) => onChangeCategory({ categoryName: value, isParent: true })}
              />
            </div>
          </div>
        </td>
      </tr>

      {/* 하위 카테고리 */}
      <tr>
        <td className="w-[150px] bg-[#F4F4F4]" />
        <td className="w-[calc(100%-150px)] border-b border-[#E8E8E8]">
          <div className="flex items-center py-[10px]">
            <div className="w-[150px]">
              <TableLabel>{languageTexts.child_category}</TableLabel>
            </div>

            <div className="w-[calc(100%-150px)]">
              <Select
                selectClassName="text-[15px] py-[9px] px-[10px]"
                bordered
                disabled={!selectedMainCategory}
                placeholder={selectedMainCategory ? languageTexts.select : languageTexts.child_category_placeholder}
                options={childCategories}
                selected={selectedSubCategory?.name || ''}
                value={selectedSubCategory?.name || ''}
                onChange={(value) => onChangeCategory({ categoryName: value, isParent: false })}
              />
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

export default memo(SelectCategories);
